<template>
    <div class="subscription-management">
        <div class="subscription-toolbar">
            <div class="segmented" role="group" aria-label="Audience">
                <button type="button" :class="['segmented-btn', { active: showInternal }]" :aria-pressed="showInternal" @click="onAudienceChange(true)">Internal</button>
                <button type="button" :class="['segmented-btn', { active: !showInternal }]" :aria-pressed="!showInternal" @click="onAudienceChange(false)">External</button>
            </div>

            <div v-if="showInternal" class="segmented" role="group" aria-label="Channel">
                <button v-for="channel in channelOptions"
                        :key="channel.value"
                        type="button"
                        :class="['segmented-btn', { active: selectedSubscriptionType === channel.value }]"
                        :aria-pressed="selectedSubscriptionType === channel.value"
                        @click="onChannelChange(channel.value)">{{ channel.label }}</button>
            </div>

            <span class="k-textbox subscription-search">
                <k-input placeholder="Search name, subject or category"
                         :value="searchWord"
                         :icon-name="'search'"
                         @input="onSearchChange"></k-input>
            </span>

            <button v-if="archivedCount > 0"
                    type="button"
                    :class="['segmented-btn', 'archived-toggle', { active: showArchived }]"
                    :aria-pressed="showArchived"
                    @click="onToggleArchived">{{ showArchived ? 'Hide' : 'Show' }} archived ({{ archivedCount }})</button>

            <div class="status-summary">
                <span><b>{{ statusCounts.subscribed }}</b> on</span>
                <span><b>{{ statusCounts.partial }}</b> partial</span>
                <span><b>{{ statusCounts.unsubscribed }}</b> off</span>
            </div>

            <div v-if="selectedNotificationType === 'email'" class="bulk-actions">
                <k-button class="subscribe-all-btn" @click="onSubscribeAllNotis" :disabled="subscribeAllBtnDisabled">Subscribe All</k-button>
                <k-button class="unsubscribe-all-btn" @click="onUnsubscribeAllNotis" :disabled="unsubscribeAllBtnDisabled">Unsubscribe All</k-button>
            </div>
        </div>

        <div class="subscription-panes">
            <div class="notification-list" role="listbox" aria-label="Notifications">
                <template v-for="group in notificationGroups" :key="group.name">
                    <button type="button"
                            class="group-header"
                            :style="{ '--group-color': group.color }"
                            :aria-expanded="isGroupOpen(group.name)"
                            @click="onToggleGroup(group.name)">
                        <span class="group-swatch" aria-hidden="true">{{ group.initials }}</span>
                        <span class="group-text">
                            <span class="group-name">{{ group.name }}</span>
                            <span class="group-meta">{{ group.onCount }} of {{ group.items.length }} on<template v-if="group.description"> · {{ group.description }}</template></span>
                        </span>
                        <i class="fa-solid fa-chevron-down group-caret" aria-hidden="true"></i>
                    </button>
                    <button v-for="noti in (isGroupOpen(group.name) ? group.items : [])"
                            :key="noti.id"
                            type="button"
                            role="option"
                            :aria-selected="noti.id === selectedNotification?.id"
                            :class="['notification-item', { selected: noti.id === selectedNotification?.id, archived: noti.is_archived }]"
                            @click="onSelectNotification(noti)">
                        <span class="notification-item-title">{{ noti.title }}</span>
                        <span :class="['status-dot', statusClass(getStatus(noti))]" :title="getStatus(noti)"></span>
                    </button>
                </template>
                <p v-if="!loading && notificationGroups.length === 0" class="empty-text">
                    {{ searchWord ? 'No notifications match your search.' : 'No notifications to show.' }}
                </p>
            </div>

            <div class="notification-detail" v-if="selectedNotification">
                <div class="detail-meta">
                    <span class="chip group-chip">{{ selectedNotification.group_name || 'Other' }}</span>
                    <span v-if="selectedNotification.recipient" class="chip">{{ selectedNotification.recipient }}</span>
                    <span :class="['chip', statusClass(selectedStatus)]">{{ selectedStatus }}</span>
                    <span v-if="selectedNotification.is_archived" class="chip archived-chip">Archived</span>
                    <span v-if="selectedNotification.sort_order" class="sort-order">Order {{ selectedNotification.sort_order }}</span>
                </div>

                <h3 class="detail-title">{{ selectedNotification.title }}</h3>

                <div class="switch-row">
                    <div>
                        <p class="switch-label">{{ switchLabel }}</p>
                        <p class="switch-help">{{ switchHelp }}</p>
                    </div>
                    <div class="switch-actions">
                        <k-button v-if="selectedStatus === 'Partially Unsubscribed'" class="turn-on-btn" @click="onSubscribeNotification">Turn on for all loans</k-button>
                        <k-switch :on-label="''"
                                  :off-label="''"
                                  :checked="selectedStatus !== 'Unsubscribed'"
                                  :aria-label="switchLabel"
                                  @change="onToggleSubscription"></k-switch>
                    </div>
                </div>

                <div v-if="showMutedLoans" class="muted-loans">
                    <div class="muted-loans-header">
                        <p class="muted-loans-title">Turned off for these loans</p>
                        <k-button class="manage-loans-btn" @click="toggleEditSubscriptionDialog">Manage loans</k-button>
                    </div>
                    <p v-for="loan in mutedLoans" :key="loan.loanId" class="muted-loan">
                        <span>{{ loan.borrowerFirst }} {{ loan.borrowerLast }}</span>
                        <span class="loan-number">{{ loan.loanNumber }}</span>
                    </p>
                    <p v-if="mutedLoans.length === 0" class="switch-help">No loans are turned off.</p>
                </div>

                <div class="preview">
                    <p class="preview-bar" v-if="previewIsEmail">Subject: <span>{{ selectedNotification.subject }}</span></p>
                    <p class="preview-bar" v-else>{{ selectedSubscriptionType === 'SMS Notifications' ? 'SMS message' : 'Push message' }}</p>
                    <iframe v-if="previewIsEmail && selectedNotification.html"
                            class="preview-frame"
                            sandbox=""
                            title="Email preview"
                            :srcdoc="selectedNotification.html"></iframe>
                    <p v-else class="preview-text">{{ previewText || 'No preview available.' }}</p>
                </div>
            </div>
            <div class="notification-detail empty-text" v-else-if="!loading">Select a notification to see its details.</div>
        </div>

        <edit-subscription-dialog :show-dialog="showEditSubscriptionDialog"
                                  :notification="selectedNotification"
                                  :unsubscribed-from-all-emails="unsubscribedFromAllEmailNotis"
                                  :unsubscribed-from-all-emails-item="unsubscribedFromAllEmailItem"
                                  :subscribed-to-all-sms="subscribedToAllSMS"
                                  :subscribed-to-all-sms-item="subscribedToAllSMSItem"
                                  :subscribed-to-all-push="subscribedToAllPush"
                                  :subscribed-to-all-push-item="subscribedToAllPushItem"
                                  :all-notifications="currentNotifications"
                                  :azure-id="azureId"
                                  :current-subscription-status="selectedStatus"
                                  :notification-type="selectedNotificationType"
                                  :notification-subscriptions="getNotificationSubscriptions()"
                                  :loading-update="loadingUpdate"
                                  @dialogclosed="toggleEditSubscriptionDialog"
                                  @subscriptionsupdated="onSubscriptionsUpdated"></edit-subscription-dialog>

        <confirm-dialog :showDialog="showConfirmDialog"
                        :dialog-Msg="confirmMsg"
                        @dialogclosed="toggleConfirmDialog"
                        @cancelclicked="toggleConfirmDialog"
                        @confirmclicked="confirmFunction"></confirm-dialog>

        <div class="component__loader-container" v-if="loading">
            <k-loader size="large" type="infinite-spinner"></k-loader>
        </div>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>

        <k-notification-group class="default__notification-container">
            <k-fade :appear="successNotification">
                <k-notification v-if="successNotification"
                                :type="{ style: 'success', icon: true }"
                                :closable="true"
                                @close="onCloseNotification('successNotification')">
                    <span>{{ notificationMsg }}</span>
                </k-notification>
            </k-fade>
        </k-notification-group>
    </div>
</template>

<script>
    import { Loader } from '@progress/kendo-vue-indicators';
    import { Dialog } from '@progress/kendo-vue-dialogs';
    import { Input, Switch } from '@progress/kendo-vue-inputs';
    import { Button } from '@progress/kendo-vue-buttons';
    import { Fade } from '@progress/kendo-vue-animation';
    import { Notification, NotificationGroup } from '@progress/kendo-vue-notification';

    import confirmDialog from '../components/confirmDialog.vue';
    import editSubscriptionDialog from './editSubscriptionDialog.vue';

    import cloneDeep from 'lodash.clonedeep';

    import apiService from '../api/api.service.js';
    import auth from '../authConfig.js';

    import subscriptionHelpers from '../helpers/subscriptionHelpers.js';

    const OTHER_GROUP = 'Other';
    const GROUP_COLORS = ['#2f6fed', '#5b4fd6', '#0e8a7b', '#d0631b', '#9a6a00', '#b4237a', '#4d7c0f', '#0284c7'];
    const OTHER_COLOR = '#64748b';

    export default {
        name: 'SubscriptionManagement',
        emits: ['notisloaded'],
        data() {
            return {
                allNotifications: [],
                azureId: null,
                channelOptions: [
                    { label: 'Email', value: 'Email Notifications' },
                    { label: 'SMS', value: 'SMS Notifications' },
                    { label: 'Push', value: 'Push Notifications' }
                ],
                confirmFunction: null,
                confirmMsg: '',
                emailSubscriptions: [],
                errorMsg: '',
                errorTitle: '',
                externalSubscriptions: [],
                groupOpenState: {},
                loading: true,
                loadingUpdate: false,
                notificationMsg: '',
                pushSubscriptions: [],
                searchWord: '',
                selectedNotificationId: null,
                selectedNotiRows: [],
                selectedSubscriptionType: 'Email Notifications',
                showArchived: false,
                showConfirmDialog: false,
                showEditSubscriptionDialog: false,
                showErrorDialog: false,
                showInternal: true,
                smsSubscriptions: [],
                successNotification: false
            };
        },
        computed: {
            archivedCount() {
                return this.currentNotifications.filter(noti => noti.is_archived).length;
            },
            currentNotifications() {
                if (!this.showInternal) {
                    return this.externalNotifications;
                }

                if (this.selectedSubscriptionType === 'SMS Notifications') {
                    return this.smsNotifications;
                }

                if (this.selectedSubscriptionType === 'Push Notifications') {
                    return this.pushNotifications;
                }

                return this.emailNotifications;
            },
            emailNotifications() {
                return this.allNotifications.filter(noti => noti.html && noti.category === 'internal');
            },
            externalNotifications() {
                return this.allNotifications.filter(noti => noti.category !== 'internal');
            },
            filteredNotifications() {
                const search = this.searchWord.trim().toLowerCase();

                if (!search) {
                    return this.listedNotifications;
                }

                return this.listedNotifications.filter(noti => {
                    return [noti.title, noti.subject, noti.group_name, noti.recipient, noti.sms_message, noti.push_message]
                        .some(val => val && val.toString().toLowerCase().includes(search));
                });
            },
            listedNotifications() {
                return this.showArchived ? this.currentNotifications : this.currentNotifications.filter(noti => !noti.is_archived);
            },
            mutedLoans() {
                return this.getNotificationSubscriptions().filter(sub => sub.loanNumber);
            },
            notificationGroups() {
                const groups = new Map();

                this.filteredNotifications.forEach(noti => {
                    const name = noti.group_name || OTHER_GROUP;

                    if (!groups.has(name)) {
                        groups.set(name, {
                            description: noti.group_description || (noti.group_name ? '' : 'Not in a category yet'),
                            items: [],
                            name,
                            sortOrder: noti.group_name ? (noti.group_sort_order ?? 0) : Number.MAX_SAFE_INTEGER
                        });
                    }

                    groups.get(name).items.push(noti);
                });

                return [...groups.values()]
                    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                    .map((group, index) => ({
                        ...group,
                        color: group.name === OTHER_GROUP ? OTHER_COLOR : GROUP_COLORS[index % GROUP_COLORS.length],
                        initials: group.name.split(/[\s&-]+/).filter(Boolean).slice(0, 2).map(word => word[0].toUpperCase()).join(''),
                        onCount: group.items.filter(noti => this.getStatus(noti) !== 'Unsubscribed').length,
                        items: [...group.items].sort((a, b) => {
                            const aOrder = a.sort_order ?? Number.MAX_SAFE_INTEGER;
                            const bOrder = b.sort_order ?? Number.MAX_SAFE_INTEGER;

                            return aOrder - bOrder || (a.title || '').localeCompare(b.title || '');
                        })
                    }));
            },
            previewIsEmail() {
                return this.selectedNotificationType === 'email' || this.selectedNotificationType === 'external';
            },
            previewText() {
                if (this.selectedNotificationType === 'SMS') {
                    return this.selectedNotification?.sms_message;
                }

                if (this.selectedNotificationType === 'Push') {
                    return this.selectedNotification?.push_message;
                }

                return '';
            },
            pushNotifications() {
                return this.allNotifications.filter(noti => noti.push_message && noti.category === 'internal');
            },
            selectedNotification() {
                const all = this.notificationGroups.flatMap(group => group.items);
                const expanded = this.notificationGroups.filter(group => this.isGroupOpen(group.name)).flatMap(group => group.items);

                return all.find(noti => noti.id === this.selectedNotificationId) || expanded[0] || all[0] || null;
            },
            selectedNotificationType() {
                if (this.showInternal) {
                    if (this.selectedSubscriptionType === 'SMS Notifications') {
                        return 'SMS';
                    }

                    if (this.selectedSubscriptionType === 'Push Notifications') {
                        return 'Push';
                    }

                    return 'email';
                }

                return 'external';
            },
            selectedStatus() {
                return this.selectedNotification ? this.getStatus(this.selectedNotification) : '';
            },
            showMutedLoans() {
                return this.selectedStatus === 'Partially Unsubscribed' && this.previewIsEmail;
            },
            smsNotifications() {
                return this.allNotifications.filter(noti => noti.sms_message && noti.category === 'internal');
            },
            statusCounts() {
                return this.listedNotifications.reduce((counts, noti) => {
                    const status = this.getStatus(noti);

                    if (status === 'Subscribed') counts.subscribed++;
                    if (status === 'Partially Unsubscribed') counts.partial++;
                    if (status === 'Unsubscribed') counts.unsubscribed++;

                    return counts;
                }, { partial: 0, subscribed: 0, unsubscribed: 0 });
            },
            statusField() {
                if (this.selectedNotificationType === 'SMS') {
                    return 'smsSubscriptionStatus';
                }

                if (this.selectedNotificationType === 'Push') {
                    return 'pushSubscriptionStatus';
                }

                if (this.selectedNotificationType === 'external') {
                    return 'externalSubscriptionStatus';
                }

                return 'subscriptionStatus';
            },
            subscribeAllBtnDisabled() {
                return this.emailSubscriptions?.length === 0;
            },
            subscribedToAllPush() {
                return this.pushSubscriptions?.some(setting => !setting.templateId && !setting.optOut);
            },
            subscribedToAllPushItem() {
                return this.pushSubscriptions?.find(setting => !setting.templateId && !setting.optOut);
            },
            subscribedToAllSMS() {
                return this.smsSubscriptions?.some(setting => !setting.templateId && !setting.optOut);
            },
            subscribedToAllSMSItem() {
                return this.smsSubscriptions?.find(setting => !setting.templateId && !setting.optOut);
            },
            switchHelp() {
                if (this.selectedStatus === 'Unsubscribed') {
                    return 'Off for all loans.';
                }

                if (this.selectedStatus === 'Partially Unsubscribed') {
                    return 'On, except for the loans listed below.';
                }

                return 'On for all loans.';
            },
            switchLabel() {
                if (this.selectedNotificationType === 'external') {
                    return 'Send this notification to my borrowers and partners';
                }

                if (this.selectedNotificationType === 'SMS') {
                    return 'Send me this notification by text';
                }

                if (this.selectedNotificationType === 'Push') {
                    return 'Send me this notification as a push alert';
                }

                return 'Send me this notification by email';
            },
            unsubscribeAllBtnDisabled() {
                return this.unsubscribedFromAllEmailNotis;
            },
            unsubscribedFromAllEmailItem() {
                return this.emailSubscriptions?.find(setting => !setting.templateId && !setting.loanId);
            },
            unsubscribedFromAllEmailNotis() {
                return this.emailSubscriptions?.some(setting => !setting.templateId && !setting.loanId);
            }
        },
        async mounted() {
            try {
                const msalAppInstance = await auth.init();
                const tokenRes = await msalAppInstance.handleRedirectPromise();

                if (!tokenRes && !auth.user()) {
                    auth.login();
                    return;
                }

                await this.loadSubscriptionData();
            } catch (error) {
                this.onRequestError('Failed to Load Subscriptions', error?.message);
            } finally {
                this.loading = false;
            }
        },
        methods: {
            assignNotificationTemplates(templatesData) {
                templatesData.forEach(template => {
                    if (template.category === 'internal') {
                        let subscriptionStatus = this.unsubscribedFromAllEmailNotis ? 'Unsubscribed' : '';
                        let smsSubscriptionStatus = this.subscribedToAllSMS ? 'Subscribed' : 'Unsubscribed';
                        let pushSubscriptionStatus = this.subscribedToAllPush ? 'Subscribed' : 'Unsubscribed';

                        if (template.html) {
                            if (!subscriptionStatus) {
                                const unsubscribedFullyFromTemplate = this.emailSubscriptions.some(setting => setting.templateId?.toLowerCase() === template.id?.toLowerCase() && !setting.loanId);

                                if (unsubscribedFullyFromTemplate) {
                                    subscriptionStatus = 'Unsubscribed';
                                } else {
                                    const unsubscribedPartiallyFromTemplate = this.emailSubscriptions.some(setting => setting.templateId?.toLowerCase() === template.id?.toLowerCase() && setting.loanId);

                                    subscriptionStatus = unsubscribedPartiallyFromTemplate ? 'Partially Unsubscribed' : 'Subscribed';
                                }
                            }

                            template.subscriptionStatus = subscriptionStatus;
                        }

                        if (template.sms_message) {
                            if (this.smsSubscriptions.some(setting => setting.templateId?.toLowerCase() === template.id?.toLowerCase() && setting.optOut === false)) {
                                smsSubscriptionStatus = 'Subscribed';
                            }

                            if (this.smsSubscriptions.some(setting => setting.templateId?.toLowerCase() === template.id?.toLowerCase() && setting.loanId)) {
                                smsSubscriptionStatus = 'Partially Unsubscribed';
                            }

                            if (this.smsSubscriptions.some(setting => !setting.templateId && !setting.loanId && setting.optOut === false)) {
                                smsSubscriptionStatus = 'Unsubscribed';
                            }

                            template.smsSubscriptionStatus = smsSubscriptionStatus;
                        }

                        if (template.push_message) {
                            if (this.pushSubscriptions.some(setting => setting.templateId?.toLowerCase() === template.id?.toLowerCase() && setting.optOut === false)) {
                                pushSubscriptionStatus = 'Subscribed';
                            }

                            if (this.pushSubscriptions.some(setting => setting.templateId?.toLowerCase() === template.id?.toLowerCase() && setting.loanId)) {
                                pushSubscriptionStatus = 'Partially Unsubscribed';
                            }

                            if (this.pushSubscriptions.some(setting => !setting.templateId && !setting.loanId && setting.optOut === false)) {
                                pushSubscriptionStatus = 'Unsubscribed';
                            }

                            template.pushSubscriptionStatus = pushSubscriptionStatus;
                        }
                    } else if (template.category !== 'utility') {
                        let externalSubscriptionStatus = 'Subscribed';

                        if (this.externalSubscriptions.some(setting => setting.templateId?.toLowerCase() === template.id?.toLowerCase() && setting.loanId)) {
                            externalSubscriptionStatus = 'Partially Unsubscribed';
                        }

                        if (this.externalSubscriptions.some(setting => setting.templateId?.toLowerCase() === template.id?.toLowerCase() && !setting.loanId)) {
                            externalSubscriptionStatus = 'Unsubscribed';
                        }

                        template.externalSubscriptionStatus = externalSubscriptionStatus;
                    }

                    template.displayName = `${template.title} - ${template.category}`;
                    template.selected = false;
                });

                // The CC tabs list notifications alphabetically, so they get their own sorted copy.
                const byDisplayName = [...templatesData].sort((a, b) => a.displayName.localeCompare(b.displayName));

                this.$emit('notisloaded', cloneDeep(byDisplayName));
                this.allNotifications = templatesData;
            },
            async deleteExternalNotificationOptOuts(subIds) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.deleteExternalNotificationOptOuts(subIds, tokenResponse.accessToken);
                } catch (error) {
                    this.onUpdateError(error);
                }
            },
            async deleteNotificationOptOuts(subIds) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.deleteNotificationOptOuts(subIds, tokenResponse.accessToken);
                } catch (error) {
                    this.onUpdateError(error);
                }
            },
            async fetchSubscriptionData() {
                await Promise.all([
                    this.getUsersNotificationSubscriptionSettings(),
                    this.getUsersExternalSubscriptionSettings(),
                    this.getUsersSMSPushSubscriptionSettings()
                ]);

                // Statuses are worked out from the settings, so templates load last.
                await this.getNotificationTemplates();
            },
            async finishUpdate() {
                try {
                    await this.onSubscriptionsUpdated();

                    if (!this.showErrorDialog) {
                        this.showNotification('Subscriptions updated successfully');
                    }
                } finally {
                    this.selectedNotiRows = [];
                    this.loading = false;
                }
            },
            getEmailSubscriptionCreates(subscriptionStatus) {
                let creates = [];

                if (subscriptionStatus === 'Subscribed' && this.unsubscribedFromAllEmailNotis) {
                    const subscribedNotiIds = this.selectedNotiRows.map(row => row.id);

                    creates = this.emailNotifications.filter(noti => !subscribedNotiIds.some(id => id === noti.id)).map(noti => {
                        return {
                            guid: null,
                            azureId: this.azureId,
                            templateId: noti.id,
                            loanId: null,
                            type: 'email'
                        };
                    });
                }

                if (subscriptionStatus === 'Unsubscribed') {
                    creates = this.selectedNotiRows.map(noti => {
                        return {
                            guid: null,
                            azureId: this.azureId,
                            templateId: noti.id,
                            loanId: null,
                            type: 'email'
                        };
                    });
                }

                return creates;
            },
            getEmailSubscriptionDeletes(subscriptionStatus) {
                let deletes = [];

                if (subscriptionStatus === 'Subscribed' && this.unsubscribedFromAllEmailItem) {
                    deletes.push(this.unsubscribedFromAllEmailItem.guid);
                }

                this.selectedNotiRows.forEach(row => {
                    const subIds = this.emailSubscriptions
                        .filter(s => s.templateId?.toLowerCase() === row.id?.toLowerCase())
                        .map(sub => sub.guid);

                    deletes = [...deletes, ...subIds];
                });

                return deletes;
            },
            getExternalSubscriptionCreates(subscriptionStatus) {
                if (subscriptionStatus !== 'Unsubscribed') {
                    return [];
                }

                return this.selectedNotiRows.map(noti => {
                    return {
                        id: null,
                        azureId: this.azureId,
                        templateId: noti.id,
                        loanId: null
                    };
                });
            },
            getExternalSubscriptionDeletes() {
                let deletes = [];

                this.selectedNotiRows.forEach(row => {
                    const subIds = this.externalSubscriptions
                        .filter(s => s.templateId?.toLowerCase() === row.id?.toLowerCase())
                        .map(sub => sub.id);

                    deletes = [...deletes, ...subIds];
                });

                return deletes;
            },
            getNotificationSubscriptions() {
                const templateId = this.selectedNotification?.id?.toLowerCase();

                if (this.selectedNotificationType === 'SMS') {
                    return this.smsSubscriptions.filter(s => s.templateId?.toLowerCase() === templateId);
                }

                if (this.selectedNotificationType === 'Push') {
                    return this.pushSubscriptions.filter(s => s.templateId?.toLowerCase() === templateId);
                }

                if (this.selectedNotificationType === 'external') {
                    return this.externalSubscriptions.filter(s => s.templateId?.toLowerCase() === templateId);
                }

                return this.emailSubscriptions.filter(s => s.templateId?.toLowerCase() === templateId);
            },
            async getNotificationTemplates() {
                const tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetNotificationTemplates(tokenResponse.accessToken)
                    .then(res => {
                        if (res.data?.data) {
                            this.assignNotificationTemplates(res.data.data);
                        }
                    }).catch(error => {
                        this.onFetchError('Failed to Fetch Notification Templates', error);
                    });
            },
            getSMSPushSubscriptionCreates(subscriptionStatus) {
                const allSubscriptions = this.selectedNotificationType === 'SMS' ? this.smsSubscriptions : this.pushSubscriptions;
                let creates = [];

                this.selectedNotiRows.forEach(row => {
                    const notificationSubscriptions = allSubscriptions.filter(s => s.templateId?.toLowerCase() === row.id?.toLowerCase());

                    if (subscriptionStatus === 'Subscribed' && !notificationSubscriptions.some(s => !s.optOut)) {
                        creates = [...creates, {
                            guid: notificationSubscriptions[0]?.guid ?? null,
                            azureId: this.azureId,
                            loanId: null,
                            templateId: row.id,
                            type: this.selectedNotificationType,
                            optOut: false
                        }];
                    }

                    if (subscriptionStatus === 'Unsubscribed') {
                        creates = [...creates, ...notificationSubscriptions.map(sub => ({
                            ...sub,
                            guid: sub.guid ? sub.guid : null,
                            optOut: true
                        }))];
                    }
                });

                return creates;
            },
            getStatus(noti) {
                return noti?.[this.statusField] || 'Subscribed';
            },
            async getUsersExternalSubscriptionSettings() {
                const tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetUsersExternalSubscriptionSettings(this.azureId, tokenResponse.accessToken)
                    .then(res => {
                        if (res.data?.data) {
                            this.externalSubscriptions = res.data.data;
                        }
                    }).catch(error => {
                        this.onFetchError('Failed to Fetch External Subscriptions', error);
                    });
            },
            async getUsersNotificationSubscriptionSettings() {
                const tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetUsersNotificationSubscriptionSettings(this.azureId, tokenResponse.accessToken)
                    .then(res => {
                        if (res.data?.data) {
                            this.emailSubscriptions = res.data.data;
                        }
                    }).catch(error => {
                        this.onFetchError('Failed to Fetch Email Subscriptions', error);
                    });
            },
            async getUsersSMSPushSubscriptionSettings() {
                const tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetUsersSMSPushSubscriptionSettings(this.azureId, tokenResponse.accessToken)
                    .then(res => {
                        if (res.data?.data) {
                            this.pushSubscriptions = res.data.data.filter(sub => sub.type === 'Push');
                            this.smsSubscriptions = res.data.data.filter(sub => sub.type === 'SMS');
                        }
                    }).catch(error => {
                        this.onFetchError('Failed to Fetch SMS and Push Subscriptions', error);
                    });
            },
            groupStateKey(groupName) {
                return `${this.selectedNotificationType}:${groupName}`;
            },
            isGroupExpanded(groupName) {
                const key = this.groupStateKey(groupName);

                // Until someone toggles a group, only the top group in each view starts open.
                if (key in this.groupOpenState) {
                    return this.groupOpenState[key];
                }

                return groupName === this.notificationGroups[0]?.name;
            },
            isGroupOpen(groupName) {
                // Searching shows every match, even in folded groups.
                return !!this.searchWord.trim() || this.isGroupExpanded(groupName);
            },
            async loadSubscriptionData() {
                // acquireTokenResponse also refreshes azure_id in localStorage, so read it afterwards.
                const tokenResponse = await auth.acquireTokenResponse();

                if (!tokenResponse?.accessToken) {
                    throw new Error('Your sign-in could not be refreshed. Reload the page and sign in again.');
                }

                this.azureId = localStorage.getItem('azure_id');

                if (!this.azureId) {
                    throw new Error('Your user ID could not be found. Reload the page and try again.');
                }

                await this.fetchSubscriptionData();
            },
            onAudienceChange(showInternal) {
                this.showInternal = showInternal;
            },
            onChannelChange(subscriptionType) {
                this.selectedSubscriptionType = subscriptionType;
            },
            onCloseNotification(flag) {
                this[flag] = false;
            },
            onFetchError(errorTitle, error) {
                this.onRequestError(errorTitle, error.response?.data?.data ? 'Error: \n\n' + error.response.data.data : '');
            },
            onRequestError(errorTitle, errorMsg) {
                this.loading = false;
                this.loadingUpdate = false;

                this.errorTitle = errorTitle;
                this.errorMsg = errorMsg || 'Please try again';

                this.showErrorDialog = true;
            },
            onSearchChange(evt) {
                this.searchWord = evt.value;
            },
            onSelectNotification(noti) {
                this.selectedNotificationId = noti.id;
            },
            onSubscribeAllNotis() {
                this.confirmMsg = 'Are you sure you would like to subscribe to all email notifications?';
                this.confirmFunction = this.subscribeAllNotis;
                this.toggleConfirmDialog();
            },
            async onSubscribeNotification() {
                this.selectedNotiRows = [this.selectedNotification];
                this.loading = true;

                if (this.selectedNotificationType === 'email') {
                    const deletedSubscriptions = this.getEmailSubscriptionDeletes('Subscribed');
                    const createdSubscriptions = this.getEmailSubscriptionCreates('Subscribed');

                    await this.deleteNotificationOptOuts(deletedSubscriptions);
                    await this.upsertNotificationOptOuts(createdSubscriptions);
                }

                if (this.selectedNotificationType === 'SMS' || this.selectedNotificationType === 'Push') {
                    await this.upsertSMSPushOptIns(this.getSMSPushSubscriptionCreates('Subscribed'));
                }

                if (this.selectedNotificationType === 'external') {
                    await this.deleteExternalNotificationOptOuts(this.getExternalSubscriptionDeletes());
                }

                await this.finishUpdate();
            },
            async onSubscriptionsUpdated() {
                this.loadingUpdate = true;

                await this.fetchSubscriptionData();

                if (this.showEditSubscriptionDialog) {
                    this.toggleEditSubscriptionDialog();
                }

                this.loadingUpdate = false;
            },
            async onToggleSubscription() {
                if (this.selectedStatus === 'Unsubscribed') {
                    await this.onSubscribeNotification();
                } else {
                    await this.onUnsubscribeNotification();
                }
            },
            onToggleGroup(groupName) {
                this.groupOpenState = {
                    ...this.groupOpenState,
                    [this.groupStateKey(groupName)]: !this.isGroupExpanded(groupName)
                };
            },
            onToggleArchived() {
                this.showArchived = !this.showArchived;
            },
            onUnsubscribeAllNotis() {
                this.confirmMsg = 'Are you sure you would like to unsubscribe from all email notifications?';
                this.confirmFunction = this.unsubscribeAllNotis;
                this.toggleConfirmDialog();
            },
            async onUnsubscribeNotification() {
                this.selectedNotiRows = [this.selectedNotification];
                this.loading = true;

                if (this.selectedNotificationType === 'email') {
                    const deletedSubscriptions = this.getEmailSubscriptionDeletes('Unsubscribed');
                    const createdSubscriptions = this.getEmailSubscriptionCreates('Unsubscribed');

                    await this.deleteNotificationOptOuts(deletedSubscriptions);
                    await this.upsertNotificationOptOuts(createdSubscriptions);
                }

                if (this.selectedNotificationType === 'SMS' || this.selectedNotificationType === 'Push') {
                    await this.upsertSMSPushOptIns(this.getSMSPushSubscriptionCreates('Unsubscribed'));
                }

                if (this.selectedNotificationType === 'external') {
                    await this.deleteExternalNotificationOptOuts(this.getExternalSubscriptionDeletes());
                    await this.upsertExternalNotificationOptOuts(this.getExternalSubscriptionCreates('Unsubscribed'));
                }

                await this.finishUpdate();
            },
            onUpdateError(error) {
                this.onRequestError('Failed to Update Subscriptions', error.response?.data?.data ? 'Error: \n\n' + error.response.data.data : '');
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;

                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                }, 4000);
            },
            statusClass(status) {
                if (status === 'Unsubscribed') return 'status-off';
                if (status === 'Partially Unsubscribed') return 'status-partial';

                return 'status-on';
            },
            async subscribeAllNotis() {
                this.toggleConfirmDialog();
                this.loading = true;

                const deleteIds = this.emailSubscriptions?.map(sub => sub.guid);

                if (deleteIds?.length > 0) {
                    await this.deleteNotificationOptOuts(deleteIds);
                }

                await this.finishUpdate();
            },
            toggleConfirmDialog() {
                this.showConfirmDialog = !this.showConfirmDialog;

                if (!this.showConfirmDialog) {
                    this.confirmMsg = '';
                    this.confirmFunction = null;
                }
            },
            toggleEditSubscriptionDialog() {
                this.showEditSubscriptionDialog = !this.showEditSubscriptionDialog;
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if (!this.showErrorDialog) {
                    this.errorTitle = '';
                }
            },
            async unsubscribeAllNotis() {
                this.toggleConfirmDialog();
                this.loading = true;

                const deleteIds = this.emailSubscriptions?.map(sub => sub.guid);

                if (deleteIds?.length > 0) {
                    await this.deleteNotificationOptOuts(deleteIds);
                }

                await this.upsertNotificationOptOuts([{
                    guid: null,
                    azureId: this.azureId,
                    loanId: null,
                    templateId: null,
                    type: 'email'
                }]);

                await this.finishUpdate();
            },
            async upsertExternalNotificationOptOuts(request) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.upsertExternalNotificationOptOuts(request, tokenResponse.accessToken);
                } catch (error) {
                    this.onUpdateError(error);
                }
            },
            async upsertNotificationOptOuts(request) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.upsertNotificationOptOuts(request, tokenResponse.accessToken);
                } catch (error) {
                    this.onUpdateError(error);
                }
            },
            async upsertSMSPushOptIns(request) {
                if (request.length === 0) {
                    return;
                }

                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    if (this.selectedNotificationType === 'SMS') {
                        await subscriptionHelpers.upsertSMSOptIns(request, tokenResponse.accessToken);
                    } else {
                        await subscriptionHelpers.upsertPushOptIns(request, tokenResponse.accessToken);
                    }
                } catch (error) {
                    this.onUpdateError(error);
                }
            }
        },
        components: {
            'confirm-dialog': confirmDialog,
            'edit-subscription-dialog': editSubscriptionDialog,
            'k-button': Button,
            'k-dialog': Dialog,
            'k-fade': Fade,
            'k-input': Input,
            'k-loader': Loader,
            'k-notification': Notification,
            'k-notification-group': NotificationGroup,
            'k-switch': Switch
        }
    };
</script>

<style>
    @import '/src/assets/css/subscriptionManagement.css';
</style>
